/**
 * compatibilityAdapter — Assist Form Contract v1 (12/09/2026)
 *
 * O backend legado continua emitindo `fill_form` com PATCH PLANO:
 *   { item_id: 123, sets: [...] }   |   { distance: 50, stroke: 'freestyle' }
 *
 * Esta camada converte DETERMINISTICAMENTE para operations[]:
 *   escalar / null            → set
 *   array ou objeto (estrutura) → replace
 *
 * NUNCA infere add/remove/reorder por diff de arrays — essas operações só
 * aparecem quando o workflow passar a produzi-las explicitamente.
 *
 * Guarda anti-destrutiva (renderers 28/08): `replace` de coleção com []
 * é rejeitado (OPERATION_NOT_ALLOWED) — limpar será sempre operação
 * explícita, nunca implícita.
 *
 * Chaves de IDENTIDADE (athleteId, squadId, targetType, *_id) NÃO viram
 * operação: identidade só entra por request_selection → applySelection.
 * O adapter de domínio pode declarar `entityKeys` para extrair a entidade
 * alvo (ex.: item_id no editor) e `structuralKeys` para forçar replace.
 */
import { ERROR_CODES, newOperationId } from './proposalContract';

const DEFAULT_IDENTITY_KEYS = new Set(['athleteId', 'squadId', 'targetType', 'athlete_id', 'squad_id', 'local_id', '_key']);

/**
 * @param {object} patch  payload.patch do fill_form (já limpo pelo renderer)
 * @param {object} [hints]
 * @param {string[]} [hints.entityKeys]      chaves que identificam a entidade alvo (não viram op)
 * @param {string[]} [hints.structuralKeys]  chaves de coleção (sempre replace)
 * @param {string[]} [hints.identityKeys]    chaves adicionais ignoradas
 * @param {string}   [hints.source]          'conversation' | 'voice'
 * @returns {{operations:Array, rejected:Array, entityRef:object|null}}
 */
export function fillFormToOperations(patch, hints = {}) {
  const operations = [];
  const rejected = [];
  let entityRef = null;
  if (!patch || typeof patch !== 'object' || Array.isArray(patch)) {
    return { operations, rejected: [{ path: '*', code: ERROR_CODES.INVALID_TYPE, reason: 'patch não é objeto' }], entityRef };
  }
  const entityKeys = new Set(hints.entityKeys || []);
  const structural = new Set(hints.structuralKeys || []);
  const identity = new Set([...DEFAULT_IDENTITY_KEYS, ...(hints.identityKeys || [])]);
  const source = hints.source || 'conversation';

  for (const [key, value] of Object.entries(patch)) {
    if (entityKeys.has(key)) {
      entityRef = { ...(entityRef || {}), [key]: value };
      continue;
    }
    if (identity.has(key)) continue; // identidade nunca via patch
    const isStructure = structural.has(key) || Array.isArray(value) || (value !== null && typeof value === 'object');
    if (isStructure) {
      if (Array.isArray(value) && value.length === 0) {
        rejected.push({ path: key, code: ERROR_CODES.OPERATION_NOT_ALLOWED, reason: 'replace com coleção vazia é destrutivo — limpar exige operação explícita' });
        continue;
      }
      operations.push({
        operationId: newOperationId(), op: 'replace', path: key,
        proposed_value: stripLocalIdentity(value), source,
      });
    } else {
      operations.push({
        operationId: newOperationId(), op: 'set', path: key,
        proposed_value: value, source,
      });
    }
  }
  return { operations, rejected, entityRef };
}

/** local_id/_key vindos do LLM são ignorados — o adapter gera os seus. */
export function stripLocalIdentity(value) {
  if (Array.isArray(value)) return value.map(stripLocalIdentity);
  if (value && typeof value === 'object') {
    const out = {};
    for (const [k, v] of Object.entries(value)) {
      if (k === 'local_id' || k === '_key') continue;
      out[k] = stripLocalIdentity(v);
    }
    return out;
  }
  return value;
}
